import {zodResolver} from '@hookform/resolvers/zod';
import type {SignInInput} from '@aws-amplify/auth';
import {PasswordInput, TextInput} from '@mantine/core';
import {Anchor} from 'src/design/atoms/Anchor';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {signInUser} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {z} from 'zod';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const schema = z.object({
  password: z.string().min(1, 'Password is required.'),
});

type FormValues = z.infer<typeof schema>;

export const InitialSignIn: React.FC = () => {
  const navigate = useAppNavigate();

  const {
    isLoading: isAuthLoading,
    username,
    userAccountStatus,
    userMfaEnabled,
  } = useAppSelector(state => state.auth);
  const isLoading = isAuthLoading;

  const dispatch = useAppDispatch();

  const {control, handleSubmit: handleFormSubmit} = useForm<FormValues>({
    defaultValues: {
      password: '',
    },
    resolver: zodResolver(schema),
  });

  const handleSubmit = async (values: FormValues) => {
    const input = {
      username: username,
      password: values.password,
    } as SignInInput;

    void dispatch(signInUser(input));
  };

  const handleSendMagicLink = async () => {
    navigate(PathUtility.SendMagicLink());
  };

  return (
    <>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Stack>
          <TextInput label="Email" value={username || ''} disabled />
          <Controller
            name="password"
            control={control}
            render={({field, fieldState}) => (
              <PasswordInput
                label="Password"
                placeholder="Your password"
                {...field}
                error={fieldState.error?.message}
                autoFocus
              />
            )}
          />
          <Group justify="space-end">
            <Anchor
              size="sm"
              c="dimmed"
              component={Link}
              to={PathUtility.ForgotPassword()}>
              Forgot password? →
            </Anchor>
          </Group>

          <Button type="submit" fullWidth loading={isLoading}>
            Login
          </Button>

          {!userMfaEnabled && (
            <>
              <Divider />

              <Button
                fullWidth
                onClick={handleSendMagicLink}
                disabled={
                  userAccountStatus ==
                  UserValidationStatusEnum.HasActiveMagicLink
                }>
                Passwordless Login
              </Button>

              {userAccountStatus !==
              UserValidationStatusEnum.HasActiveMagicLink ? (
                <Text size="sm" c="dimmed">
                  Click the button above to send a magic login link to your
                  registered email.
                </Text>
              ) : (
                <Text size="sm" c="dimmed">
                  You already have an active magic link. Please check your
                  email.
                </Text>
              )}
            </>
          )}
        </Stack>
      </form>
    </>
  );
};
